<template>
  <div class="bg-gray-100 h-full w-full relative text-2xl">
    <!-- 顶部导航栏 -->
    <div
      class="bg-purple-600 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10"
    >
      <div class="flex items-center">
        <button class="text-white mr-3" @click="goBack">
          <i class="fas fa-chevron-left"></i>
        </button>
        <h1 class="text-xl font-semibold">{{ tableNumber }}号桌 - 结账</h1>
      </div>
      <div class="flex items-center">
        <button class="mr-4" @click="printReceipt">
          <i class="fas fa-print text-white"></i>
        </button>
        <button>
          <i class="fas fa-ellipsis-v text-white"></i>
        </button>
      </div>
    </div>

    <!-- 订单详情 -->
    <div class="bg-white p-4 mb-4 text-2xl pl-24 pr-24">
      <div class="flex justify-between items-center mb-3">
        <div class="flex items-center">
          <div class="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
            <i class="fas fa-receipt text-purple-600"></i>
          </div>
          <div>
            <h2 class="font-xl text-left">订单详情</h2>
            <p class="text-xl text-gray-500">订单号: #{{ orderNo }}</p>
          </div>
        </div>
        <span class="text-purple-600 font-medium">已用餐: {{ diningDuration }}</span>
      </div>

      <div class="border-t pt-3">
        <div class="flex justify-between mb-2">
          <span class="text-gray-600">桌号</span>
          <span>{{ tableNumber }}号桌</span>
        </div>
        <div class="flex justify-between mb-2">
          <span class="text-gray-600">用餐人数</span>
          <span>{{ peopleCount }}人</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">下单时间</span>
          <span>{{ formatDateTime(orderTime) }}</span>
        </div>
      </div>
    </div>

    <!-- 消费明细 -->
    <div class="bg-white mb-4 text-2xl pl-24 pr-24">
      <div class="py-3 border-b border-gray-100">
        <h2 class="font-semibold">消费明细</h2>
      </div>

      <div class="p4">
        <div class="space-y-3 mb-4">
          <div v-for="dish in orderDishes" :key="dish.name" class="flex justify-between">
            <div>
              <div class="font-medium text-left">
                {{ dish.name }}
                <span v-if="dish.isAddedFood" class="text-xl text-green-500">(加菜)</span>
              </div>
              <div class="text-gray-500 text-left">¥{{ dish.price }} × {{ dish.quantity }}</div>
            </div>
            <div class="text-right">
              <div>¥{{ (dish.price * dish.quantity).toFixed(2) }}</div>
            </div>
          </div>
        </div>

        <div class="border-t border-gray-100 pt-3 pb-3 space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600">
              小计
              <span class="text-base text-gray-500">（共{{ totalDishCount }}份）</span>
            </span>
            <span>¥{{ subtotal.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <div class="flex items-center">
              <span class="text-gray-600">服务费 ({{ serviceFeeRate * 100 }}%)</span>
              <button
                class="ml-2 text-purple-500 hover:text-purple-700 text-sm"
                title="点击调整服务费"
                @click="adjustServiceFee"
              >
                <i class="fas fa-edit"></i>
              </button>
            </div>
            <span>¥{{ serviceFee.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">订单总金额</span>
            <span>¥{{ totalAmount.toFixed(2) }}</span>
          </div>
          <div v-if="paymentAmount > 0" class="flex justify-between text-green-600">
            <div class="flex items-center">
              <i class="fas fa-check-circle mr-1"></i>
              <span>已付金额</span>
            </div>
            <span>¥{{ paymentAmount.toFixed(2) }}</span>
          </div>
          <div v-if="discountAmount > 0" class="flex justify-between text-red-500 items-center">
            <div class="flex items-center">
              <i class="fas fa-tag mr-1"></i>
              <span>优惠券减免</span>
            </div>
            <span>-¥{{ discountAmount.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 支付方式 -->
    <div class="bg-white mb-32 text-2xl pl-24 pr-24">
      <div class="px-4 py-3 border-b border-gray-100">
        <h2 class="font-semibold">支付方式</h2>
      </div>

      <div class="p-4 pb-8">
        <div class="payment-methods">
          <div
            v-for="method in paymentMethods"
            :key="method.type"
            class="payment-method"
            :class="{ active: selectedPayment === method.type }"
            @click="selectPayment(method.type)"
          >
            <div class="payment-icon" :class="method.iconClass">
              <i :class="method.icon"></i>
            </div>
            <div class="payment-content text-left">
              <div class="font-medium">{{ method.name }}</div>
              <div class="text-xs text-gray-500">{{ method.description }}</div>
            </div>
            <div class="payment-check">
              <i class="fas fa-check-circle"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部结算区域 -->
    <div
      class="fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-200 text-2xl pl-24 pr-24"
    >
      <div class="flex justify-between items-center mb-3">
        <div class="text-lg font-bold text-2xl">剩余应付</div>
        <div class="text-2xl font-bold text-purple-600">¥{{ finalAmount.toFixed(2) }}</div>
      </div>

      <div class="flex space-x-3">
        <button
          class="flex-1 py-3 border border-purple-600 text-purple-600 rounded-lg font-medium text-2xl"
          @click="applyDiscount"
        >
          应用优惠
        </button>
        <button
          class="flex-1 py-3 bg-purple-600 text-white rounded-lg font-medium text-2xl"
          @click="completePayment"
        >
          完成支付
        </button>
      </div>
    </div>

    <!-- 支付码弹窗 -->
    <div
      v-if="showQRModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click="hideQRCode"
    >
      <div class="bg-white rounded-xl p-6 w-80" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">{{ getPaymentMethodName(selectedPayment) }}</h3>
          <button @click="hideQRCode">
            <i class="fas fa-times text-gray-500"></i>
          </button>
        </div>
        <div class="text-center">
          <div class="font-bold text-xl mb-2">¥{{ finalAmount.toFixed(2) }}</div>
          <div class="w-48 h-48 mx-auto bg-gray-100 flex items-center justify-center mb-4">
            <i class="fas fa-qrcode text-8xl text-gray-400"></i>
          </div>
          <p class="text-gray-600 mb-2">请顾客扫描二维码完成支付</p>
          <button
            class="bg-purple-600 text-white py-2 px-4 rounded-lg w-full mt-2"
            @click="simulatePayment"
          >
            模拟支付完成
          </button>
        </div>
      </div>
    </div>

    <!-- 支付成功弹窗 -->
    <div
      v-if="showSuccessModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-xl p-6 w-80">
        <div class="text-center">
          <div
            class="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"
          >
            <i class="fas fa-check-circle text-4xl text-green-500"></i>
          </div>
          <h3 class="text-xl font-bold mb-2">支付成功</h3>
          <p class="text-gray-600 mb-4">订单已成功支付</p>
          <button
            class="bg-purple-600 text-white py-2 px-4 rounded-lg w-full"
            @click="returnToTables"
          >
            返回桌位页面
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { getOrder } from '@/service/order';
  import { showDialog } from 'vant';

  const router = useRouter();
  const route = useRoute();

  // 接口定义
  interface OrderDish {
    name: string;
    price: number;
    quantity: number;
    isAddedFood?: boolean; // 是否为加菜
  }

  interface PaymentMethod {
    type: string;
    name: string;
    description: string;
    icon: string;
    iconClass: string;
  }

  interface OrderDetail {
    orderId: number;
    orderNo: string;
    tableNumber: number;
    personCount: number;
    createTime: string;
    orderStatus: number;
    totalAmount: number;
    paymentAmount?: number; // 已支付金额
    remainAmount?: number; // 剩余应付金额
    foodList: Array<{
      foodName: string;
      shopFoodName: string;
      price: number;
      quantity: number;
      addType: number; // 1-正常，2-加菜
      amount: number;
    }>;
  }

  // 响应式数据
  const tableNumber = ref(0);
  const orderId = ref(0);
  const orderNo = ref('');
  const peopleCount = ref(0);
  const orderTime = ref('');
  const diningDuration = ref('');
  const selectedPayment = ref('wechat');
  const showQRModal = ref(false);
  const showSuccessModal = ref(false);
  const discountAmount = ref(0);
  const loading = ref(false);

  // 多次支付相关字段
  const totalAmount = ref(0); // 订单总金额
  const paymentAmount = ref(0); // 已支付金额
  const remainAmount = ref(0); // 剩余应付金额

  // 服务费配置（可调整）
  const serviceFeeRate = ref(0); // 服务费比例，0表示无服务费
  const serviceFeeModalVisible = ref(false);
  const tempServiceFeeRate = ref(0);

  // 订单菜品数据 - 改为动态数据
  const orderDishes = ref<OrderDish[]>([]);

  // 支付方式配置
  const paymentMethods = ref<PaymentMethod[]>([
    {
      type: 'employee',
      name: '员工卡',
      description: '员工卡支付',
      icon: 'fas fa-address-card',
      iconClass: 'cash',
    },
    {
      type: 'wechat',
      name: '微信支付',
      description: '使用微信扫码支付',
      icon: 'fab fa-weixin',
      iconClass: 'wechat',
    },
    {
      type: 'alipay',
      name: '支付宝',
      description: '使用支付宝扫码支付',
      icon: 'fab fa-alipay',
      iconClass: 'alipay',
    },
    {
      type: 'cash',
      name: '现金支付',
      description: '使用现金结账',
      icon: 'fas fa-money-bill-wave',
      iconClass: 'cash',
    },
    {
      type: 'card',
      name: '刷卡支付',
      description: '使用银行卡支付',
      icon: 'fas fa-credit-card',
      iconClass: 'card',
    },
  ]);

  // 计算属性
  const totalDishCount = computed(() => {
    return orderDishes.value.reduce((sum, dish) => sum + dish.quantity, 0);
  });

  const subtotal = computed(() => {
    return orderDishes.value.reduce((sum, dish) => sum + dish.price * dish.quantity, 0);
  });

  const serviceFee = computed(() => {
    return Math.round(subtotal.value * serviceFeeRate.value);
  });

  const finalAmount = computed(() => {
    // 使用API返回的剩余应付金额，如果没有则使用计算值
    if (remainAmount.value > 0) {
      return remainAmount.value;
    }
    return subtotal.value + serviceFee.value - discountAmount.value;
  });

  // 加载订单详情数据
  const loadOrderDetail = async () => {
    try {
      if (!orderId.value) {
        console.error('订单ID为空');
        return;
      }

      loading.value = true;
      const response = await getOrder({ orderId: orderId.value });

      if (response.code === 200 && response.result) {
        const orderData = response.result;

        // 更新基础信息
        orderNo.value = orderData.orderNo || '';
        // 从tableName提取桌号数字，如果tableName是"3号桌"格式
        if (orderData.tableName) {
          const tableMatch = orderData.tableName.match(/(\d+)/);
          tableNumber.value = tableMatch ? parseInt(tableMatch[1]) : 0;
        }
        peopleCount.value = orderData.personCount || 0;

        // 更新支付相关字段
        totalAmount.value = orderData.totalAmount || 0;
        paymentAmount.value = orderData.paymentAmount || 0;
        remainAmount.value = orderData.remainAmount || 0;

        // 处理时间格式
        if (orderData.createTime) {
          if (orderData.createTime instanceof Date) {
            orderTime.value = formatDateTime(orderData.createTime.getTime());
          } else if (typeof orderData.createTime === 'string') {
            orderTime.value = formatDateTime(orderData.createTime);
          }
        }

        // 计算用餐时长
        if (orderData.createTime) {
          let orderDate: Date;
          if (orderData.createTime instanceof Date) {
            orderDate = orderData.createTime;
          } else {
            orderDate = new Date(orderData.createTime);
          }
          const now = new Date();
          const diffMs = now.getTime() - orderDate.getTime();
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          diningDuration.value = `${diffHours}小时${diffMinutes}分钟`;
        }

        // 转换菜品数据
        if (orderData.foodList && Array.isArray(orderData.foodList)) {
          orderDishes.value = orderData.foodList.map((food: any) => ({
            name: food.foodName || food.shopFoodName || '',
            price: food.price || 0,
            quantity: food.quantity || 1,
            isAddedFood: food.addType === 2, // addType: 1-正常，2-加菜
          }));
        }

        console.log('订单详情加载成功:', orderData);
        console.log('转换后的菜品数据:', orderDishes.value);
      } else {
        showDialog({ message: response.msg || '加载订单详情失败' });
      }
    } catch (error) {
      console.error('加载订单详情失败:', error);
      showDialog({ message: '加载订单详情失败，请稍后重试' });
    } finally {
      loading.value = false;
    }
  };

  // 方法
  const formatDateTime = (dateTime: string | number) => {
    let date: Date;

    if (typeof dateTime === 'string') {
      date = new Date(dateTime);
    } else if (typeof dateTime === 'number') {
      date = new Date(dateTime);
    } else {
      return '';
    }

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  const selectPayment = (paymentType: string) => {
    selectedPayment.value = paymentType;
  };

  const getPaymentMethodName = (type: string) => {
    const method = paymentMethods.value.find((m) => m.type === type);
    return method ? method.name : '支付';
  };

  const completePayment = () => {
    if (
      selectedPayment.value === 'cash' ||
      selectedPayment.value === 'card' ||
      selectedPayment.value === 'employee'
    ) {
      // 现金、刷卡或员工卡支付直接显示成功
      showPaymentSuccess();
    } else {
      // 显示支付二维码
      showQRCode();
    }
  };

  const showQRCode = () => {
    showQRModal.value = true;
  };

  const hideQRCode = () => {
    showQRModal.value = false;
  };

  const simulatePayment = () => {
    hideQRCode();
    showPaymentSuccess();
  };

  const showPaymentSuccess = () => {
    showSuccessModal.value = true;
  };

  const returnToTables = () => {
    router.push('/tableManage');
  };

  const applyDiscount = () => {
    alert('优惠券功能将在下一版本推出');
  };

  const printReceipt = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentTime = new Date();

    printWindow.document.write(`
    <html>
      <head>
        <title>结账小票</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 14px; margin: 20px; }
          .receipt-header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; margin-bottom: 15px; }
          .receipt-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
          .receipt-item { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .receipt-footer { border-top: 2px dashed #000; padding-top: 10px; margin-top: 15px; text-align: center; font-size: 12px; }
          .total-line { border-top: 1px solid #000; padding-top: 8px; margin-top: 8px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="receipt-header">
          <div class="receipt-title">美食集市 - 结账小票</div>
          <div>订单号: #${orderNo.value}</div>
        </div>

        <div class="receipt-item">
          <span>桌号:</span>
          <span>${tableNumber.value}号桌</span>
        </div>
        <div class="receipt-item">
          <span>用餐人数:</span>
          <span>${peopleCount.value}人</span>
        </div>
        <div class="receipt-item">
          <span>下单时间:</span>
          <span>${orderTime.value}</span>
        </div>
        <div class="receipt-item">
          <span>支付方式:</span>
          <span>${getPaymentMethodName(selectedPayment.value)}</span>
        </div>

        <div style="margin: 15px 0; text-align: center; font-weight: bold;">消费明细</div>

        ${orderDishes.value
          .map(
            (dish) => `
          <div class="receipt-item">
            <span>${dish.name}${dish.isAddedFood ? '(加菜)' : ''} x${dish.quantity}</span>
            <span>¥${(dish.price * dish.quantity).toFixed(2)}</span>
          </div>
        `,
          )
          .join('')}

        <div class="receipt-item">
          <span>小计:</span>
          <span>¥${subtotal.value.toFixed(2)}</span>
        </div>
        <div class="receipt-item">
          <span>服务费 (${serviceFeeRate.value * 100}%):</span>
          <span>¥${serviceFee.value.toFixed(2)}</span>
        </div>
        <div class="receipt-item">
          <span>订单总金额:</span>
          <span>¥${totalAmount.value.toFixed(2)}</span>
        </div>
        ${
          paymentAmount.value > 0
            ? `
        <div class="receipt-item">
          <span>已付金额:</span>
          <span>¥${paymentAmount.value.toFixed(2)}</span>
        </div>
        `
            : ''
        }
        ${
          discountAmount.value > 0
            ? `
        <div class="receipt-item">
          <span>优惠减免:</span>
          <span>-¥${discountAmount.value.toFixed(2)}</span>
        </div>
        `
            : ''
        }

        <div class="receipt-footer">
          <div class="total-line" style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">
            本次支付金额: ¥${finalAmount.value.toFixed(2)}
          </div>
          <div>打印时间: ${currentTime.toLocaleString()}</div>
          <div style="margin-top: 10px;">谢谢惠顾，欢迎再次光临！</div>
        </div>
      </body>
    </html>
  `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const goBack = () => {
    router.back();
  };

  const adjustServiceFee = () => {
    // 实现调整服务费的逻辑
    console.log('调整服务费');
  };

  // 生命周期
  onMounted(async () => {
    // 从URL获取参数
    const tableParam = route.query.table;
    const orderIdParam = route.query.orderId;

    if (tableParam) {
      tableNumber.value = parseInt(tableParam as string);
    }

    if (orderIdParam) {
      orderId.value = parseInt(orderIdParam as string);
      // 加载订单详情
      await loadOrderDetail();
    } else {
      showDialog({ message: '缺少订单ID参数' });
    }
  });
</script>

<style scoped>
  .payment-methods {
    display: flex;
    flex-direction: column;
  }

  .payment-method {
    display: flex;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .payment-method:last-child {
    border-bottom: none;
  }

  .payment-method:hover {
    /* background-color: #f9fafb; */
  }

  .payment-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    font-size: 20px;
    color: white;
  }

  .payment-icon.wechat {
    background-color: #2dc100;
  }

  .payment-icon.alipay {
    background-color: #00a0e9;
  }

  .payment-icon.cash {
    background-color: #ff9500;
  }

  .payment-icon.card {
    background-color: #007aff;
  }

  .payment-content {
    flex: 1;
  }

  .payment-check {
    font-size: 18px;
    color: #8b5cf6;
    visibility: hidden;
  }

  .payment-method.active .payment-check {
    visibility: visible;
  }

  .payment-method.active {
    /* background-color: #f3f4f6; */
  }
</style>
